import React from 'react'
import styled from 'styled-components'
import Button from '@material-ui/core/Button'
import {useAttendeeVariables} from 'Event'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import {useToggle} from 'lib/toggle'
import {Ordered} from 'lib/list'
import {RemoveButton} from 'organization/Event/DashboardConfig/ConfigDialog'
import HTMLContent from 'lib/ui/form/HTMLEditor/HTMLContent'
import {useSimpleBlogTemplate} from 'Event/template/SimpleBlog'
import {HasOffset} from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem'
import {TextConfig} from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem/Text/TextConfig'
import Section, {
  SectionBox,
} from 'Event/template/SimpleBlog/Dashboard/Sidebar/Section'
import {useEditMode} from 'Event/EditModeProvider'
import {HasSchedule} from 'lib/ui/layout/Scheduled'
import {HasRules} from 'Event/attendee-rules'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useRemoveTranslations} from 'Event/LanguageProvider'

export const TEXT = 'Text'

export interface TextProps extends Ordered, HasSchedule, HasOffset, HasRules {
  type: typeof TEXT
  padding: number
  body: string
  isEnabled: boolean
}

export const createText = (): TextProps => ({
  type: TEXT,
  padding: 5,
  body: 'This is sample text.  Click Edit Text to modify this text.',
  isEnabled: true,
  offsetTop: 0,
  offsetBottom: 0,
})

export default function Text(props: TextProps & {id: string}) {
  const {sidebar} = useSimpleBlogTemplate()
  const {body, padding, isEnabled, offsetTop, offsetBottom} = props
  const v = useAttendeeVariables()
  const {flag: configVisible, toggle: toggleConfig} = useToggle()
  const isEditMode = useEditMode()

  if (!isEditMode && !isEnabled) {
    return null
  }

  return (
    // Disable side padding to allow text to go all all the
    // way to border.
    <Section offsetTop={offsetTop} offsetBottom={offsetBottom}>
      <Body
        aria-label="text"
        color={sidebar.textColor}
        linkColor={sidebar.linkTextColor}
        padding={padding}
      >
        {v(body)}
      </Body>
      <EditModeOnly>
        <TextConfig
          id={props.id}
          showing={configVisible}
          onClose={toggleConfig}
          text={props}
        />
        <SectionBox disablePaddingY>
          <EditButton
            onClick={toggleConfig}
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            aria-label="edit text"
          >
            Edit Text
          </EditButton>
          <RemoveTextButton id={props.id} />
        </SectionBox>
      </EditModeOnly>
    </Section>
  )
}

function RemoveTextButton(props: {id: string}) {
  const {id} = props
  const saveTemplate = useSaveTemplate()
  const removeTranslations = useRemoveTranslations()

  const remove = () => {
    saveTemplate({
      sidebarItems: {
        [id]: REMOVE,
      },
      localization: removeTranslations([`${id}_body`]),
    })
  }

  return (
    <RemoveButton size="large" showing onClick={remove}>
      Remove Text
    </RemoveButton>
  )
}

const Body = styled(HTMLContent)<{
  color: string | null
  linkColor: string | null
  padding: number
}>`
  color: ${(props) => props.color};
  padding: ${(props) => props.padding}px;

  a {
    color: ${(props) => props.linkColor};
  }
`

const EditButton = styled(Button)`
  margin-bottom: ${(props) => props.theme.spacing[2]}!important;
`
